import type { GridApi } from 'ag-grid-enterprise'
import { uuidv7 } from 'posthog-js/lib/src/uuidv7'
import { parseYYYYMM } from '~/composables/commons'
import { getRowIdToUseForUndoRedo } from '~/composables/planning-helpers'
import type { PlanningRowData } from '~/composables/unified-edit-api'
import { useAppSettings } from '~/composables/useAppSettings'
import { getDisplayDateFromPlanningBegin } from '~/composables/usePlanningDateUtils'
import { useCtxStore } from '~/stores/ctx.store.ts'
import { type SeasonalityLogic, createPlanningViewStore } from '~/stores/planningview.store'

// ─────────────────────────────────────────────────────────────
// Default Logics (hardcoded, never stored)
// ─────────────────────────────────────────────────────────────
const DEFAULT_LOGICS: SeasonalityLogic[] = [
  { id: 'evenly', name: 'spread-evenly', type: 'default' },
  { id: 'l12m', name: 'spread-according-to-l12m', type: 'default' },
]

// ─────────────────────────────────────────────────────────────
// Value Calculation Functions (evenly/l12m - never stored)
// ─────────────────────────────────────────────────────────────

/**
 * Calculate seasonality values based on logic type
 * - evenly: 100/12 for each month
 * - l12m: Based on last 12 months actuals distribution
 */
export function getDefaultValues(logicId: string, rowId?: string): string[] {
  if (logicId === 'evenly') {
    const val = (100 / 12).toString()
    return new Array(12).fill(val)
  }
  if (logicId === 'l12m' && rowId) {
    return calculateL12MDistribution(rowId)
  }
  return new Array(12).fill('0')
}

export function generateL12MPeriod(lastActualDateStr: string): string[] {
  const endDate = parseYYYYMM(lastActualDateStr)
  const l12mPeriod: string[] = []

  for (let i = 11; i >= 0; i--) {
    const date = new Date(endDate)
    date.setMonth(date.getMonth() - i)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    l12mPeriod.push(`${year}-${month}`)
  }

  return l12mPeriod
}

export function calculateL12MSum(l12mPeriod: string[], rowActuals: Record<string, number>): number {
  return l12mPeriod.reduce((sum, monthKey) => sum + (rowActuals[monthKey] || 0), 0)
}

export function findCalendarMonthActual(calendarMonth: number, l12mPeriod: string[], rowActuals: Record<string, number>): number {
  const monthStr = String(calendarMonth).padStart(2, '0')
  for (let i = l12mPeriod.length - 1; i >= 0; i--) {
    const periodMonth = l12mPeriod[i]
    if (periodMonth.endsWith(`-${monthStr}`)) {
      return rowActuals[periodMonth] || 0
    }
  }
  return 0
}

export function calculateMonthlyDistribution(l12mPeriod: string[], rowActuals: Record<string, number>, totalSum: number): string[] {
  const values: string[] = []
  for (let calendarMonth = 1; calendarMonth <= 12; calendarMonth++) {
    const y = findCalendarMonthActual(calendarMonth, l12mPeriod, rowActuals)
    const value = (y / totalSum) * 100
    values.push(value.toString())
  }
  return values
}

export function calculateL12MDistribution(rowId: string): string[] {
  try {
    const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
    const lastActualDate = computed(() => getDisplayDateFromPlanningBegin(planningStore.selectedPlan?.fpna_begin_date_string))

    const currentRow = planningStore.selectedPlanState?.pnlList.find((r) => r.row_id === rowId)
    if (!currentRow?.path_level || currentRow.path_level.length === 0) {
      return getDefaultValues('evenly')
    }

    const parentPathLevel = currentRow.path_level.slice(0, -1)
    const parentRow = planningStore.selectedPlanState?.pnlList.find((r) => r.row_id === currentRow.path_level[parentPathLevel.length - 1])

    const parentRowId = parentRow?.row_id
    if (!parentRowId) {
      return getDefaultValues('evenly')
    }

    const l12mPeriod = generateL12MPeriod(lastActualDate.value)
    const rowActuals = planningStore.selectedPlanState?.valuesMapPnl?.[parentRowId]?.values_map || {}
    const totalSum = calculateL12MSum(l12mPeriod, rowActuals)

    if (Math.abs(totalSum) < 0.01) {
      return getDefaultValues('evenly')
    }

    return calculateMonthlyDistribution(l12mPeriod, rowActuals, totalSum)
  } catch (_error) {
    return getDefaultValues('evenly')
  }
}

// ─────────────────────────────────────────────────────────────
// Logic List Functions
// ─────────────────────────────────────────────────────────────

/**
 * Get all available seasonality logics:
 * - Default: evenly, l12m (hardcoded)
 * - Custom saved: from planConfigCache.seas_map
 * - Custom pending: from pendingCustomSeasonality
 */
export function getAllSeasonalityLogics(): SeasonalityLogic[] {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  const logics: SeasonalityLogic[] = [...DEFAULT_LOGICS]
  let deletedCount = 0

  // Get custom logics from planConfigCache (saved to backend)
  if (selectedPlan?.id) {
    const planConfig = planningStore.planConfigCache[selectedPlan.id]
    const seasMap = planConfig?.stcs_comp_plan_cfg?.seas_map
    const i18nMap = planConfig?.stcs_comp_plan_cfg?.i18n_map

    if (seasMap) {
      Object.keys(seasMap).forEach((key) => {
        if (key.startsWith('custom_') && !logics.some((l) => l.id === key)) {
          const ctxStore = useCtxStore()
          const orgId = ctxStore.active_org_id
          const name = i18nMap?.[orgId]?.label_map?.[key]
          // biome-ignore lint/style/noNegationElse: <explanation>
          if (!name) {
            deletedCount++
            logics.push({ id: key, name: `Deleted Seasonality Logic (${deletedCount})`, type: 'custom' })
          } else {
            logics.push({ id: key, name, type: 'custom' })
          }
        }
      })
    }
  }

  // Get custom logics from pendingCustomSeasonality (not yet saved)
  Object.keys(planningStore.pendingCustomSeasonality).forEach((key) => {
    if (key.startsWith('custom_') && !logics.some((l) => l.id === key)) {
      // Get name from i18n_map if available
      const planConfig = planningStore.planConfigCache[selectedPlan?.id || '']
      const i18nMap = planConfig?.stcs_comp_plan_cfg?.i18n_map
      const ctxStore = useCtxStore()
      const orgId = ctxStore.active_org_id
      const name = i18nMap?.[orgId]?.label_map?.[key]
      // biome-ignore lint/style/noNegationElse: <explanation>
      if (!name) {
        deletedCount++
        logics.push({ id: key, name: `Deleted Seasonality Logic (${deletedCount})`, type: 'custom' })
      } else {
        logics.push({ id: key, name, type: 'custom' })
      }
    }
  })

  return logics
}

// ─────────────────────────────────────────────────────────────
// Selection Functions (using seas_entry_map)
// ─────────────────────────────────────────────────────────────

/**
 * Get the currently selected seasonality logic ID for a row
 */
export function getSelectedLogicId(rowId: string): string {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const entry = planningStore.seas_entry_map[rowId]

  if (!entry?.logic_key) {
    return 'evenly'
  }

  if (entry.logic_key === 'custom') {
    return entry.seas_key?.startsWith('custom_') ? entry.seas_key : 'evenly'
  }

  return entry.logic_key
}

/**
 * Set the selected seasonality logic for a row
 * Handles both UI formats ('evenly', 'l12m', 'custom_xxx') and backend formats ('evenly', 'l12m_xxxxx', 'custom_xxx')
 */
export function setSelectedLogic(rowId: string, logicId: string): void {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)

  // Determine logic_key from logicId pattern
  // - 'l12m' or 'l12m_xxxxx' → 'l12m'
  // - 'custom_xxx' → 'custom'
  // - 'evenly' or anything else → 'evenly'
  const logicKey = logicId.includes('l12m') ? 'l12m' : logicId.startsWith('custom_') ? 'custom' : 'evenly'

  planningStore.seas_entry_map[rowId] = {
    logic_key: logicKey,
    seas_key: logicId,
  }
}
/**
 * Initialize a row with seasonality - uses saved preference if available, otherwise defaults to evenly
 */
export function initializeRowSeasonality(rowId: string): void {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  if (!planningStore.seas_entry_map[rowId]) {
    const savedPreference = getLastSelectedSeasonality()

    // Use saved preference or default to evenly
    const logicId = savedPreference || 'evenly'
    const logicKey = logicId.includes('l12m') ? 'l12m' : logicId.startsWith('custom_') ? 'custom' : 'evenly'

    planningStore.seas_entry_map[rowId] = {
      logic_key: logicKey,
      seas_key: logicId,
    }
  }
}

/**
 * Get the last selected seasonality preference for the current plan
 * Returns: 'evenly', 'l12m', or 'custom_xxxxx'
 */
export function getLastSelectedSeasonality(): string | undefined {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const planId = planningStore.selectedPlanID

  if (!planId) return undefined

  const preference = getSeasonalityPreferenceFromSettings(ctxStore.active_comp_id, planId)
  return preference
}

/**
 * Save the last selected seasonality preference for the current plan
 * Only saves for evenly, l12m (as 'l12m'), and custom_xxx (full key)
 */
export function saveLastSelectedSeasonality(logicId: string): void {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const planId = planningStore.selectedPlanID

  if (!planId) return

  // Normalize the key to save:
  // - 'evenly' → 'evenly'
  // - 'l12m' or 'l12m_xxxxx' → 'l12m'
  // - 'custom_xxxxx' → 'custom_xxxxx' (full key)
  let preferenceToSave: string
  if (logicId.includes('l12m')) {
    preferenceToSave = 'l12m'
  } else if (logicId.startsWith('custom_')) {
    preferenceToSave = logicId
  } else {
    preferenceToSave = 'evenly'
  }

  saveSeasonalityPreferenceToSettings(ctxStore.active_comp_id, planId, preferenceToSave)
}

/**
 * Get seasonality preference from user settings
 */
function getSeasonalityPreferenceFromSettings(companyId: string, planId: string): string | undefined {
  const { parseUserSectionSettings } = useAppSettings()
  const planSettings = parseUserSectionSettings(companyId, 'plan')
  return planSettings?.ref_plans_map?.[planId]?.selected_seasonality
}

/**
 * Save seasonality preference to user settings
 */
function saveSeasonalityPreferenceToSettings(companyId: string, planId: string, preference: string): void {
  const { parseUserSectionSettings, saveUserSectionSettingsForPlan } = useAppSettings()
  const currentSettings = parseUserSectionSettings(companyId, 'plan')

  const updatedSettings = {
    ...currentSettings,
    ref_plans_map: {
      ...currentSettings.ref_plans_map,
      [planId]: {
        ...currentSettings.ref_plans_map?.[planId],
        selected_seasonality: preference,
      },
    },
  }

  saveUserSectionSettingsForPlan(companyId, 'plan', updatedSettings)
}

// ─────────────────────────────────────────────────────────────
// Value Retrieval Functions
// ─────────────────────────────────────────────────────────────

/**
 * Get seasonality values for the currently selected logic of a row
 * Priority:
 * 1. evenly/l12m: Calculate on demand (never stored)
 * 2. custom (pending): From pendingCustomSeasonality
 * 3. custom (saved): From planConfigCache.seas_map
 */
export function getSeasonalityValues(rowId: string): string[] {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  const logicId = getSelectedLogicId(rowId)

  // Default logics - calculate on demand
  if (logicId === 'evenly' || logicId === 'l12m') {
    return getDefaultValues(logicId, rowId)
  }

  // Custom logic - check pending first, then cache
  if (logicId.startsWith('custom_')) {
    // Check pendingCustomSeasonality first (unsaved changes)
    const pendingValues = planningStore.pendingCustomSeasonality[logicId]
    if (pendingValues && pendingValues.length === 12) {
      return pendingValues
    }

    // Check planConfigCache (saved values)
    const planConfig = planningStore.planConfigCache[selectedPlan?.id || '']
    const seasMap = planConfig?.stcs_comp_plan_cfg?.seas_map?.[logicId]
    if (seasMap?.seas_input_map) {
      return Object.values(seasMap.seas_input_map).map((v: number) => v.toString())
    }
  }

  // Fallback to evenly
  return getDefaultValues('evenly', rowId)
}

/**
 * Get values for a specific logic (used by modal for display)
 */
export function getValuesForLogic(rowId: string, logicId: string): string[] {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState

  // Default logics - calculate on demand
  if (logicId === 'evenly' || logicId === 'l12m') {
    return getDefaultValues(logicId, rowId)
  }

  // Custom logic
  if (logicId.startsWith('custom_')) {
    // Check pendingCustomSeasonality first
    const pendingValues = planningStore.pendingCustomSeasonality[logicId]
    if (pendingValues && pendingValues.length === 12) {
      return pendingValues
    }

    // Check planConfigCache
    const planConfig = planningStore.planConfigCache[selectedPlan?.id || '']
    const seasMap = planConfig?.stcs_comp_plan_cfg?.seas_map?.[logicId]
    if (seasMap?.seas_input_map) {
      return Object.values(seasMap.seas_input_map).map((v: number) => v.toString())
    }
  }

  return getDefaultValues('evenly', rowId)
}

// ─────────────────────────────────────────────────────────────
// Value Update Functions
// ─────────────────────────────────────────────────────────────

/**
 * Set seasonality values for the currently selected custom logic
 * Only works for custom logics - evenly/l12m values are calculated, not stored
 */
export function setSeasonalityValues(rowId: string, values: string[]): void {
  const logicId = getSelectedLogicId(rowId)

  // Only store custom values - evenly/l12m are calculated
  if (!logicId.startsWith('custom_')) {
    return
  }

  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  planningStore.pendingCustomSeasonality[logicId] = values
}

/**
 * Set values for a specific custom logic (keyed by seas_key)
 */
export function setValuesForCustomLogic(seasKey: string, values: string[]): void {
  if (!seasKey.startsWith('custom_')) {
    return
  }

  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  planningStore.pendingCustomSeasonality[seasKey] = values
}

// ─────────────────────────────────────────────────────────────
// Custom Logic CRUD Functions
// ─────────────────────────────────────────────────────────────

/**
 * Helper to initialize i18n_map structure in plan config
 */
function ensureI18nMapStructure(planConfig: ReturnType<typeof createPlanningViewStore>['planConfigCache'][string], orgId: string): void {
  if (!planConfig?.stcs_comp_plan_cfg) return

  planConfig.stcs_comp_plan_cfg.i18n_map ??= {}
  planConfig.stcs_comp_plan_cfg.i18n_map[orgId] ??= { label_map: {} }
  planConfig.stcs_comp_plan_cfg.i18n_map[orgId].label_map ??= {}
}

/**
 * Add a new custom seasonality logic
 */
export function addCustomSeasonalityLogic(rowId: string, name: string, copyFromCurrentLogic = false): { success: boolean; error?: string; id?: string } {
  if (!name.trim()) {
    return { success: false, error: 'Name required' }
  }

  const logics = getAllSeasonalityLogics()
  if (logics.some((l) => l.name.toLowerCase() === name.trim().toLowerCase())) {
    return { success: false, error: 'Name must be unique' }
  }

  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  const id = `custom_${uuidv7()}`

  // Initialize values - copy from current logic or use evenly
  const initialValues: string[] = copyFromCurrentLogic ? getValuesForLogic(rowId, getSelectedLogicId(rowId)) : getDefaultValues('evenly', rowId)

  // Store values in pendingCustomSeasonality
  planningStore.pendingCustomSeasonality[id] = initialValues

  // Store name in i18n_map (in planConfigCache)
  if (selectedPlan?.id) {
    const planConfig = planningStore.planConfigCache[selectedPlan.id]
    const ctxStore = useCtxStore()
    ensureI18nMapStructure(planConfig, ctxStore.active_org_id)
    if (planConfig?.stcs_comp_plan_cfg?.i18n_map?.[ctxStore.active_org_id]?.label_map) {
      planConfig.stcs_comp_plan_cfg.i18n_map[ctxStore.active_org_id].label_map[id] = name.trim()
    }
  }

  // Set as selected logic for this row
  setSelectedLogic(rowId, id)

  return { success: true, id }
}

/**
 * Edit custom seasonality logic name
 */
export function editCustomSeasonalityLogic(_rowId: string, logicId: string, newName: string): { success: boolean; error?: string } {
  if (!newName.trim()) {
    return { success: false, error: 'Name required' }
  }

  const logics = getAllSeasonalityLogics()
  const logic = logics.find((l) => l.id === logicId && l.type === 'custom')
  if (!logic) {
    return { success: false, error: 'Logic not found' }
  }

  if (logics.some((l) => l.name.toLowerCase() === newName.trim().toLowerCase() && l.id !== logicId)) {
    return { success: false, error: 'Name must be unique' }
  }

  // Update name in i18n_map
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  if (selectedPlan?.id) {
    const planConfig = planningStore.planConfigCache[selectedPlan.id]
    const ctxStore = useCtxStore()
    ensureI18nMapStructure(planConfig, ctxStore.active_org_id)
    if (planConfig?.stcs_comp_plan_cfg?.i18n_map?.[ctxStore.active_org_id]?.label_map) {
      planConfig.stcs_comp_plan_cfg.i18n_map[ctxStore.active_org_id].label_map[logicId] = newName.trim()
    }
  }

  return { success: true }
}

/**
 * Delete custom seasonality logic
 */
export function deleteCustomSeasonalityLogic(rowId: string, logicId: string): { success: boolean; error?: string } {
  const logics = getAllSeasonalityLogics()
  const idx = logics.findIndex((l) => l.id === logicId && l.type === 'custom')
  if (idx === -1) {
    return { success: false, error: 'Logic not found' }
  }

  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState

  // Remove from pendingCustomSeasonality
  delete planningStore.pendingCustomSeasonality[logicId]

  // Remove from i18n_map
  if (selectedPlan?.id) {
    const planConfig = planningStore.planConfigCache[selectedPlan.id]
    if (planConfig?.stcs_comp_plan_cfg?.i18n_map) {
      const ctxStore = useCtxStore()
      const orgId = ctxStore.active_org_id
      if (planConfig.stcs_comp_plan_cfg.i18n_map[orgId]?.label_map) {
        delete planConfig.stcs_comp_plan_cfg.i18n_map[orgId].label_map[logicId]
      }
    }
  }

  // If deleted logic was selected, revert to evenly
  if (getSelectedLogicId(rowId) === logicId) {
    setSelectedLogic(rowId, 'evenly')
  }

  return { success: true }
}

// ─────────────────────────────────────────────────────────────
// Grid Integration Functions
// ─────────────────────────────────────────────────────────────

export function handleSeasonalityConfirm(gridApi: GridApi<PlanningRowData> | null, selectedRowId: string | null) {
  if (gridApi === null) return
  if (!selectedRowId) return

  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const rowNode = gridApi.getRowNode(selectedRowId)
  if (!rowNode) return

  planningStore.updatePlanningData(getRowIdToUseForUndoRedo(rowNode), true)
}

// ─────────────────────────────────────────────────────────────
// Payload Helper Functions (for annual-growth.ts)
// ─────────────────────────────────────────────────────────────

/**
 * Get seasonality values for payload as numeric map
 * Used when building the request payload
 */
export function getSeasonalityValuesForPayload(rowId: string): { [key: string]: number } {
  const values = getSeasonalityValues(rowId)
  return Object.fromEntries(values.map((value, index) => [(index + 1).toString(), Number(value)]))
}

/**
 * Get seasonality values for a specific seas_key (used when custom logic key is known)
 */
export function getValuesForSeasKey(rowId: string, seasKey: string): { [key: string]: number } {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState

  // Default logics
  if (seasKey === 'evenly' || seasKey.startsWith('l12m')) {
    const logicId = seasKey === 'evenly' ? 'evenly' : 'l12m'
    const values = getDefaultValues(logicId, rowId)
    return Object.fromEntries(values.map((value, index) => [(index + 1).toString(), Number(value)]))
  }

  // Custom logic
  if (seasKey.startsWith('custom_')) {
    // Check pendingCustomSeasonality first
    const pendingValues = planningStore.pendingCustomSeasonality[seasKey]
    if (pendingValues && pendingValues.length === 12) {
      return Object.fromEntries(pendingValues.map((value, index) => [(index + 1).toString(), Number(value)]))
    }

    // Check planConfigCache
    const planConfig = planningStore.planConfigCache[selectedPlan?.id || '']
    const seasMap = planConfig?.stcs_comp_plan_cfg?.seas_map?.[seasKey]
    if (seasMap?.seas_input_map) {
      return seasMap.seas_input_map
    }
  }

  // Fallback to evenly
  const evenlyValues = getDefaultValues('evenly', rowId)
  return Object.fromEntries(evenlyValues.map((value, index) => [(index + 1).toString(), Number(value)]))
}

/**
 * Get the display name of the currently selected seasonality logic for a row
 */
export function getSelectedSeasonalityName(rowId: string): string {
  const logicId = getSelectedLogicId(rowId)
  const { t } = useTranslate()
  const logics = getAllSeasonalityLogics()
  const selectedLogic = logics.find((l) => l.id === logicId)

  return selectedLogic?.id === 'l12m' ? t('spread-according-to-l12m') : selectedLogic?.id === 'evenly' ? t('spread-evenly') : selectedLogic?.name || t('spread-evenly')
}
